import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import IconButton from '@atlaskit/button/icon/button';
import AddIcon from '@atlaskit/icon/core/add';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading/heading';

type CardProps = {
	/** Section title — pass a MessageDescriptor from defineMessages. */
	title: MessageDescriptor;
	/** Count displayed as a small badge next to the title. */
	count?: number;
	/**
	 * Maximum number of items allowed. When count >= maxCount the add button
	 * is rendered in a disabled state.
	 */
	maxCount?: number;
	/** Called when the plus button is clicked. Omit to hide the button. */
	onAdd?: () => void;
	/** Custom action button rendered in the header. Omit to hide any button. Takes precedence over onAdd. */
	actionButton?: React.ReactNode;
	/** CardItem / Row children rendered in a horizontal flex row. */
	children: React.ReactNode;
	/**
	 * Wrap each child in the items row (e.g. layout animation). Uses the same flex styles as the default wrapper.
	 */
	renderItemWrapper?: (child: React.ReactElement, index: number) => React.ReactNode;
};

const i18n = defineMessages({
	add: {
		id: 'space-overview.smart-space-overview.card.add',
		defaultMessage: 'Add',
		description: 'Accessible label for the add button on a space overview card.',
	},
});

const styles = cssMap({
	card: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.xlarge'),
		padding: token('space.250'),
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		marginBottom: token('space.150'),
	},
	titleGroup: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.075'),
		flex: 1,
	},
	itemsRow: {
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: token('space.100'),
	},
	itemWrapper: {
		minWidth: '0px',
		display: 'flex',
		flexDirection: 'column',
	},
});

export const Card = ({
	title,
	count,
	maxCount,
	onAdd,
	actionButton,
	children,
	renderItemWrapper,
}: CardProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const isAtCapacity = maxCount !== undefined && count !== undefined && count >= maxCount;

	// Use actionButton if provided, otherwise fall back to onAdd for backward compatibility
	const renderActionButton = () => {
		if (actionButton) {
			return actionButton;
		}
		if (onAdd) {
			return (
				<IconButton
					icon={AddIcon}
					label={formatMessage(i18n.add)}
					appearance="subtle"
					spacing="compact"
					isDisabled={isAtCapacity}
					onClick={onAdd}
				/>
			);
		}
		return null;
	};

	return (
		<div css={styles.card}>
			<div css={styles.header}>
				<div css={styles.titleGroup}>
					<Heading as="h2" size="small">
						{formatMessage(title)}
					</Heading>
				</div>
				{renderActionButton()}
			</div>

			<div css={styles.itemsRow}>
				{React.Children.map(children, (child, index) => {
					if (!React.isValidElement(child)) {
						return child;
					}
					if (renderItemWrapper) {
						return renderItemWrapper(child, index);
					}
					return (
						<div key={child.key ?? index} css={styles.itemWrapper}>
							{child}
						</div>
					);
				})}
			</div>
		</div>
	);
};
